import "./ChartTooltipV2.scss";
import cn from "classnames";
import React from "react";

export interface PayloadItem {
  label: string | number | undefined;
  value: string | number | undefined;
  color: string | undefined;
  keyShape?: KeyShape;
  styles?: Record<string, any>;
  keyOpacity?: number;
  shape?: string;
  iconClassName?: string;
}

export enum KeyShapeOptions {
  CIRCLE = "circle",
  SQUARE = "square",
  LINE = "line",
  DASHED_LINE = "dashedLine",
  FILLED_RECT = "filledRect",
}
export type KeyShape = `${KeyShapeOptions}`;

type StringArrayOfMaxLength2 = readonly [string?, string?];
type StringOrNumberArrayOfMaxLength2 = readonly [(string | number)?, (string | number)?];

interface ChartTooltipV2Props {
  headers: StringArrayOfMaxLength2;
  subHeaderLabel?: string | React.ReactNode;
  subHeaderValue?: string | React.ReactNode;
  footerLabels?: StringArrayOfMaxLength2;
  footerValues?: StringOrNumberArrayOfMaxLength2;
  items: PayloadItem[];
  itemKeyShape?: KeyShape;
  comparisonItems?: PayloadItem[];
  comparisonItemKeyShape?: KeyShape;
  reverseItems?: boolean;
  decorateLabelsWithColon?: boolean;
}
export type TooltipPassthroughProps = Partial<Omit<ChartTooltipV2Props, "headers, items">>;

export const ChartTooltipV2: React.FC<ChartTooltipV2Props> = ({
  headers,
  subHeaderLabel = "",
  subHeaderValue = "",
  footerLabels = ["Total:", "Total:"],
  footerValues = [],
  items,
  comparisonItems,
  itemKeyShape = "line",
  comparisonItemKeyShape = "dashedLine",
  reverseItems = false,
  decorateLabelsWithColon = true,
}) => {
  const [primaryHeader, comparisonHeader] = headers;
  const [primaryFooterLabel, comparisonFooterLabel] = footerLabels;
  const [primaryFooterValue, comparisonFooterValue] = footerValues;
  const hasComparisonPeriod = Boolean(comparisonHeader && comparisonItems);
  const displayedItems = reverseItems ? [...items].reverse() : items;

  return (
    <div className="chartTooltipV2">
      <div
        className={`chartTooltipV2Container ${hasComparisonPeriod ? "hasComparisonPeriod" : ""}`}
      >
        <div className="tooltipHeader">
          {hasComparisonPeriod && <div className="headerSuperLabel">Primary Period</div>}
          {primaryHeader}
        </div>
        {(subHeaderLabel || subHeaderValue) && (
          <div className="tooltipSubheader">
            <div className="subheaderLabel">{subHeaderLabel}</div>
            <div className="subheaderValue">{subHeaderValue}</div>
          </div>
        )}
        <div className="itemList">
          {displayedItems.map((item, i) => {
            const { label, value, color, keyShape, styles, keyOpacity } = item;
            if (!value) {
              return <></>;
            }
            return (
              <div
                className={`itemRow ${hasComparisonPeriod ? "hasComparisonPeriod" : ""}`}
                key={i}
              >
                <div className="labelContainer">
                  <ColorKey shape={keyShape ?? itemKeyShape} color={color} opacity={keyOpacity} />
                  <div className="label" style={{ ...styles }}>{`${label}${
                    decorateLabelsWithColon ? ": " : ""
                  }`}</div>
                </div>
                <div className={`value ${hasComparisonPeriod ? "hasComparisonPeriod" : ""}`}>
                  {value}
                </div>
              </div>
            );
          })}
        </div>
        {primaryFooterValue && (
          <div className="footer">
            <div className="footerLabel">{primaryFooterLabel}</div>
            <div className="footerValue">{primaryFooterValue}</div>
          </div>
        )}
      </div>

      {hasComparisonPeriod && (
        <div className="chartTooltipV2Container">
          <div className="tooltipHeader">
            <div className="headerSuperLabel">Comparison Period</div>
            {comparisonHeader}
          </div>
          <div className="itemList">
            {(comparisonItems || []).map((item, i) => {
              const { label, value, color, styles } = item;
              if (!value) {
                return <></>;
              }
              return (
                <div
                  className={`itemRow ${hasComparisonPeriod ? "hasComparisonPeriod" : ""}`}
                  key={i}
                >
                  <div className="labelContainer">
                    <ColorKey shape={comparisonItemKeyShape} color={color} />
                    <div className="label" style={{ ...styles }}>{`${label}: `}</div>
                  </div>
                  <div className="value hasComparisonPeriod">{value}</div>
                </div>
              );
            })}
          </div>
          {comparisonFooterValue && (
            <div className="footer">
              <div className="footerLabel">{comparisonFooterLabel}</div>
              <div className="footerValue">{comparisonFooterValue}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ColorKeyProps {
  shape: KeyShape;
  color: string | undefined;
  opacity?: number;
}

export const ColorKey: React.FC<ColorKeyProps> = React.memo(({ shape, color, opacity = 1 }) => {
  switch (shape) {
    case "dashedLine":
      return (
        <svg
          className="colorKey dashedLine"
          viewBox="0 0 20 12"
          width={16}
          height={12}
          style={{ opacity }}
        >
          <path d="M 0 5 H 20" stroke={color} strokeWidth="4" strokeDasharray="4" />
        </svg>
      );
    // When using a filledRect, the parent component is responsible for
    // rendering a patternDefinition for the provided fill
    case "filledRect":
      return (
        <svg className="colorKey filledRect" width={16} height={12} style={{ opacity }}>
          <rect className="square" fill={color} />
        </svg>
      );
    default:
      return (
        <div
          className={cn("colorKey", { [shape]: shape })}
          style={{ backgroundColor: color, opacity }}
        />
      );
  }
});

export default ChartTooltipV2;
